/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  getDisplayProps,
  getColorProps,
  getMarginProps,
  getPaddingProps,
  getTypographyProps,
} from "../../utils/styleProps";

import { convertToCSSSize, getColor } from "../../theme/theme";
import { BoxBaseProps } from "./Box.types";
import { ThemeMode } from "../../theme/theme.types";
import styled from "@emotion/styled";

// Extended props that include theme from Emotion's ThemeProvider
interface StyledBoxProps extends BoxBaseProps {
  theme?: { mode?: ThemeMode };
}

/**
 * Converts camelCase strings to kebab-case
 * This is necessary because CSS properties in stylesheets must use kebab-case (e.g., padding-left),
 * while in JavaScript we use camelCase (e.g., paddingLeft).
 *
 * Properly handles vendor prefixes like WebKit by adding the required hyphen prefix.
 *
 * @example
 * camelToKebab('paddingLeft') => 'padding-left'
 * camelToKebab('backgroundColor') => 'background-color'
 * camelToKebab('WebkitLineClamp') => '-webkit-line-clamp'
 */
const camelToKebab = (str: string): string => {
  // Special case for WebKit properties
  if (str.startsWith("Webkit")) {
    return "-" + str.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
  }
  // Regular camelCase to kebab-case conversion
  return str.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
};

/**
 * Helper function to merge style objects without overriding with undefined values
 * This ensures that styles set by style utility functions aren't overwritten by undefined props
 */
const mergeStyles = (
  baseStyles: Record<string, any>,
  propStyles: Record<string, any>
): Record<string, any> => {
  const result = { ...baseStyles };

  // Only add prop style values that are defined
  Object.entries(propStyles).forEach(([key, value]) => {
    if (value !== undefined) {
      result[key] = value;
    }
  });

  return result;
};

// Define styled component for Box base styles
// Note: The StyledBox is still based on 'div' as it will be transformed via 'as' prop
export const StyledBox = styled("div")<StyledBoxProps>`
  box-sizing: border-box;
  min-width: 0;

  /* Apply styles from styleProps utility functions */
  ${(props: StyledBoxProps) => {
    // Get theme mode from Emotion's theme context
    const themeMode = props.theme?.mode;

    // Get computed style properties
    const displayStyles = getDisplayProps(props);
    const marginStyles = getMarginProps(props);
    const paddingStyles = getPaddingProps(props);
    const colorStyles = getColorProps(props, themeMode);
    const typographyStyles = getTypographyProps(props);

    // Start with styles from utility functions
    let combinedStyles = {
      ...displayStyles,
      ...marginStyles,
      ...paddingStyles,
      ...typographyStyles,
      ...colorStyles,
    };

    // Merge layout properties without overriding with undefined values
    combinedStyles = mergeStyles(combinedStyles, {
      width: convertToCSSSize(props.width),
      height: convertToCSSSize(props.height),
      minWidth: convertToCSSSize(props.minWidth),
      maxWidth: convertToCSSSize(props.maxWidth),
      minHeight: convertToCSSSize(props.minHeight),
      maxHeight: convertToCSSSize(props.maxHeight),
      overflow: props.overflow,
      overflowX: props.overflowX,
      overflowY: props.overflowY,
    });

    // Merge flexbox properties
    combinedStyles = mergeStyles(combinedStyles, {
      alignItems: props.alignItems,
      justifyContent: props.justifyContent,
      flexDirection: props.flexDirection,
      flexWrap: props.flexWrap,
      flex: props.flex,
      flexGrow: props.flexGrow,
      flexShrink: props.flexShrink,
      flexBasis: convertToCSSSize(props.flexBasis),
      gap: convertToCSSSize(props.gap),
      rowGap: convertToCSSSize(props.rowGap),
      columnGap: convertToCSSSize(props.columnGap),
    });

    // Merge grid properties
    combinedStyles = mergeStyles(combinedStyles, {
      gridTemplateColumns: props.gridTemplateColumns,
      gridTemplateRows: props.gridTemplateRows,
      gridTemplateAreas: props.gridTemplateAreas,
      gridArea: props.gridArea,
      gridAutoFlow: props.gridAutoFlow,
      gridAutoRows: props.gridAutoRows,
      gridAutoColumns: props.gridAutoColumns,
      gridColumn: props.gridColumn,
      gridRow: props.gridRow,
    });

    // Merge positioning properties
    combinedStyles = mergeStyles(combinedStyles, {
      position: props.position,
      top: convertToCSSSize(props.top),
      right: convertToCSSSize(props.right),
      bottom: convertToCSSSize(props.bottom),
      left: convertToCSSSize(props.left),
      zIndex: props.zIndex,
    });

    // Merge border and styling properties
    combinedStyles = mergeStyles(combinedStyles, {
      border: props.border,
      borderTop: props.borderTop,
      borderRight: props.borderRight,
      borderBottom: props.borderBottom,
      borderLeft: props.borderLeft,
      borderColor: props.borderColor
        ? getColor(props.borderColor, themeMode)
        : undefined,
      borderRadius:
        typeof props.borderRadius === "number"
          ? `${props.borderRadius}px`
          : props.borderRadius,
      boxShadow: props.boxShadow,
      textShadow: props.textShadow,
    });

    // Merge interactivity properties
    combinedStyles = mergeStyles(combinedStyles, {
      cursor: props.cursor,
      userSelect: props.userSelect,
      resize: props.resize,
      transition: props.transition,
      outline: props.outline,
      outlineOffset:
        typeof props.outlineOffset === "number"
          ? `${props.outlineOffset}px`
          : props.outlineOffset,
    });

    // Convert camelCase to kebab-case and build CSS rules
    const cssRules = Object.entries(combinedStyles)
      .filter(([, value]) => value !== undefined)
      .map(([prop, value]) => `${camelToKebab(prop)}: ${value};`);

    return cssRules.join("\n");
  }}
`;
